/**
 * Dynamic Yield Product Recommendations Web Component
 *
 * Fetches product recommendations from the DY Choose API response and renders
 * full Shopify product cards in a carousel using the Section Rendering API.
 *
 * @example
 * <dy-product-recommendations
 *   data-dy-selector="pdp-recs"
 * ></dy-product-recommendations>
 */

/** @typedef {{ slotId: string, productData: { url: string, sku: string, name: string, price: number, image_url: string, group_id: string } }} DYSlot */
/** @typedef {{ html: string, slotId: string, handle: string, sku: string }} ProductCard */

const MOBILE_BREAKPOINT = 750;

class DYProductRecommendations extends HTMLElement {
  /**
   * Cached product card HTML by handle
   * @type {Map<string, string>}
   */
  #cardCache = new Map();

  /**
   * Slot data for click tracking (slotId by product handle)
   * @type {Map<string, string>}
   */
  #slotIdMap = new Map();

  /**
   * AbortController for fetch requests
   * @type {AbortController | null}
   */
  #abortController = null;

  /**
   * IntersectionObserver for lazy loading
   * @type {IntersectionObserver | null}
   */
  #observer = null;

  /**
   * Decision ID for engagement tracking (CLICK events on carousel controls)
   * @type {string | null}
   */
  #decisionId = null;

  /**
   * Choice ID (choice.id) for the _productRecDY line item property
   * @type {string | null}
   */
  #choiceId = null;

  /**
   * Tracks which swatch containers already have a click listener to prevent double-binding
   * @type {WeakSet<Element>}
   */
  #initializedSwatchContainers = new WeakSet();

  /**
   * PDP URLs already prefetched to avoid duplicate <link rel="prefetch"> elements.
   * @type {Set<string>}
   */
  #prefetchedPdpUrls = new Set();

  /**
   * Per-container prefetch state: card HTML cache + in-flight abort controller + hover timer.
   * Keyed by the .product-card-swatches container element.
   * @type {WeakMap<Element, { cache: Map<string, Element>, abortController: AbortController|null, timeoutId: ReturnType<typeof setTimeout>|null }>}
   */
  #swatchPrefetchState = new WeakMap();

  /**
   * Cached DY API slot data for re-rendering after section morph resets the component.
   * Set to null when no recommendations were loaded.
   * @type {DYSlot[] | null}
   */
  #cachedSlots = null;

  /**
   * Cached custom slide data from DY payload, stored alongside #cachedSlots.
   * @type {CustomSlideData | null}
   */
  #cachedCustomData = null;

  /**
   * ResizeObserver for carousel navigation arrow state updates on container resize.
   * @type {ResizeObserver | null}
   */
  #navigationResizeObserver = null;

  static observedAttributes = ['data-loaded'];

  static #SWATCH_HOVER_DELAY_MS = 175;
  static #SWATCH_LOADING_DEFER_MS = 150;
  static #SWATCH_CACHE_MAX = 10;
  static #preconnectAdded = false;

  /**
   * Eagerly resolved DY data (slots, decisionId, choiceId, customData) cached before
   * the IntersectionObserver fires, so card fetches can start immediately on intersection.
   * @type {Promise<{ slots: DYSlot[], decisionId: string|null, choiceId: string|null, customData: any } | null> | null}
   */
  #earlyDyData = null;

  connectedCallback() {
    // Eagerly resolve DY data so it's ready when the section enters the viewport.
    // This overlaps DY SDK wait time with scrolling, eliminating the DY.Experience.ready
    // bottleneck from the critical rendering path.
    this.#earlyDyData = this.#resolveDyData();

    // Inject a preconnect hint for Section Rendering API fetches so the TLS handshake
    // is already complete by the time we start fetching product cards.
    if (!DYProductRecommendations.#preconnectAdded) {
      DYProductRecommendations.#preconnectAdded = true;
      if (!document.querySelector(`link[rel="preconnect"][href="${window.location.origin}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = window.location.origin;
        document.head.appendChild(link);
      }
    }

    // Skip lazy loading for priority (above-the-fold) sections
    if (this.dataset.priority === 'high') {
      this.#loadRecommendations();
      return;
    }

    // Use IntersectionObserver to lazy load when section is near viewport
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            this.#observer?.disconnect();
            this.#observer = null;
            this.#loadRecommendations();
          }
        },
        { rootMargin: '600px' } // Start loading 600px before visible
      );
      this.#observer.observe(this);
    } else {
      // Fallback for older browsers without IntersectionObserver
      this.#loadRecommendations();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
    this.#navigationResizeObserver?.disconnect();
  }

  /**
   * Whether this component lives inside a <dialog> element.
   * Cached on first access since the ancestor chain doesn't change.
   * @type {boolean | null}
   */
  #isInsideDialog = null;

  /**
   * @returns {boolean}
   */
  get #inDialog() {
    if (this.#isInsideDialog === null) {
      this.#isInsideDialog = !!this.closest('dialog');
    }
    return this.#isInsideDialog;
  }

  /**
   * When DY recommendations are rendered inside a <dialog> (e.g. cart drawer, search drawer),
   * the standard quick-add overlay would be hidden behind the dialog backdrop.
   *
   * This method rewires quick-add buttons by replacing each one with a clean clone.
   * Cloning strips the direct `handleClick` listener attached by quick-add-component's
   * connectedCallback, and removing the `on:click` attribute prevents the theme framework's
   * capture-phase delegation from routing to the quick-add overlay. A new direct listener
   * opens the mini-pdp-drawer instead.
   *
   * @param {HTMLElement} container - The container with newly-rendered product cards
   */
  #rewireQuickAddForDialog(container) {
    if (!this.#inDialog) return;

    const buttons = container.querySelectorAll('.quick-add__button');
    buttons.forEach((btn) => {
      const productCard = btn.closest('product-card');
      if (!productCard) return;

      const cleanBtn = /** @type {HTMLElement} */ (btn.cloneNode(true));
      cleanBtn.removeAttribute('on:click');
      cleanBtn.removeAttribute('data-listener-attached');
      btn.replaceWith(cleanBtn);

      cleanBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = this.#getHandleFromCard(productCard);
        if (handle) this.#openMiniPdpForProduct(handle);
      });
    });
  }

  /**
   * Reads the current product handle from a product card element.
   * Uses the product title link href which is kept in sync by #updateProductCard
   * on swatch changes, unlike quick-add-component's data-product-handle which is stale.
   * @param {Element} productCard
   * @returns {string | null}
   */
  #getHandleFromCard(productCard) {
    const link = productCard.querySelector('.product-card__title');
    const href = link?.getAttribute('href');
    if (href) {
      const handle = this.#extractProductHandle(href);
      if (handle) return handle;
    }
    const quickAdd = productCard.querySelector('quick-add-component');
    return /** @type {HTMLElement | null} */ (quickAdd)?.dataset?.productHandle ?? null;
  }

  /**
   * Fetches mini-pdp content for a product and opens the mini-pdp-drawer.
   * Closes the parent dialog first (search drawer, cart drawer, etc.) so the
   * mini-pdp drawer isn't hidden behind it.
   * @param {string} handle
   */
  async #openMiniPdpForProduct(handle) {
    const miniPdpDrawerEl = /** @type {{ open?: (e: { target: HTMLElement }) => void } | null} */ (
      document.getElementById('mini-pdp-drawer')
    );
    if (!miniPdpDrawerEl || typeof miniPdpDrawerEl.open !== 'function') return;
    const openMiniPdp = miniPdpDrawerEl.open.bind(miniPdpDrawerEl);

    const html = await this.#fetchMiniPdpContent(handle);
    if (!html) return;

    // Module <script> tags inside template.innerHTML are inert (won't execute).
    // Extract them and dynamically import so custom elements (e.g. mini-pdp-variant-selector)
    // are registered before the content is inserted into the DOM.
    await this.#loadModuleScripts(html);

    const trigger = document.createElement('div');
    trigger.dataset.productHandle = handle;
    trigger.dataset.returnButtonLabel = 'back';
    const template = document.createElement('template');
    template.setAttribute('data-mini-pdp-content', '');
    template.innerHTML = html;
    trigger.appendChild(template);

    const parentDialog = this.closest('dialog');
    const parentComponent = /** @type {HTMLElement & { closeDialog?: () => void } | null} */ (
      parentDialog?.parentElement
    );
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    if (parentDialog?.open && parentComponent && typeof parentComponent.closeDialog === 'function') {
      if (!isMobile) {
        // Desktop: open mini-pdp stacked on top — parent drawer stays open.
        // Set data-stacked after open() clears it but before showDialog's rAF fires,
        // so the backdrop is suppressed to avoid double-darkening.
        openMiniPdp({ target: trigger });
        const miniPdpDialog = /** @type {any} */ (miniPdpDrawerEl).refs?.dialog;
        if (miniPdpDialog instanceof HTMLElement) {
          miniPdpDialog.setAttribute('data-stacked', '');
        }
      } else {
        // Mobile: close parent first, then open mini-pdp, reopen parent on close.
        if (parentComponent.matches?.('cart-drawer-component')) {
          trigger.dataset.reopenCartDrawer = '';
        } else if (parentComponent.id === 'search-modal') {
          trigger.dataset.reopenSearchDrawer = '';
        }
        parentComponent.addEventListener(
          'dialog:close',
          () => { requestAnimationFrame(() => openMiniPdp({ target: trigger })); },
          { once: true }
        );
        parentComponent.closeDialog();
      }
    } else {
      openMiniPdp({ target: trigger });
    }
  }

  /**
   * Dynamically imports module scripts found in the HTML content.
   * When HTML is set via template.innerHTML, <script type="module"> tags are inert.
   * This ensures custom elements (e.g. mini-pdp-variant-selector) are registered.
   * @param {string} html
   */
  async #loadModuleScripts(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const scripts = doc.querySelectorAll('script[type="module"][src]');
    const imports = [];
    for (const script of scripts) {
      const src = script.getAttribute('src');
      if (!src) continue;
      const tagName = src.match(/\/([a-z-]+)\.js/)?.[1];
      if (tagName && customElements.get(tagName)) continue;
      // @ts-expect-error Dynamic import is supported at runtime in theme script context
      imports.push(import(src).catch(() => {}));
    }
    if (imports.length > 0) await Promise.all(imports);
  }

  /**
   * Fetches pre-rendered mini-pdp content for a product via the Section Rendering API.
   * Strips the outer .shopify-section wrapper that the API adds, since
   * mini-pdp-drawer expects the raw mini-pdp-content markup as a direct child
   * of its content container (the wrapper breaks the height: 100% chain).
   * @param {string} handle - Product handle
   * @returns {Promise<string | null>}
   */
  async #fetchMiniPdpContent(handle) {
    try {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const url = new URL(`${routeRoot}products/${handle}`, window.location.origin);
      url.searchParams.set('section_id', 'section-rendering-mini-pdp');
      const response = await fetch(url.toString());
      if (!response.ok) return null;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('.shopify-section');
      return wrapper ? wrapper.innerHTML : html;
    } catch {
      return null;
    }
  }

  /**
   * Detects when section morphing strips data-loaded and restores the loaded state from cache.
   * When the cart updates, morphSection re-renders the section with server HTML (skeleton state).
   * The morph removes data-loaded and resets the carousel's inline style to "display: none",
   * making the skeleton visible and hiding the loaded recommendations.
   * We recover by re-rendering from the cached DY response — no network requests since
   * product card HTML is already in #cardCache.
   *
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-loaded' && oldValue === 'true' && newValue === null) {
      queueMicrotask(() => this.#restoreAfterMorph());
    }
  }

  /**
   * Restore the component to its loaded state after a section morph reset.
   * If children are still present (morph skipped subtree), just restores attributes.
   * Otherwise re-renders product cards from the cached DY response data.
   */
  #restoreAfterMorph() {
    if (!this.#cachedSlots) {
      // Section was hidden (no DY recommendations); re-apply hidden state
      this.#hideSection();
      return;
    }

    const slidesContainer = this.slidesContainerEl;
    if (slidesContainer && slidesContainer.children.length > 0) {
      // Children were preserved by data-skip-subtree-update; only attributes need restoring
      this.dataset.loaded = 'true';
      this.dataset.hasRecommendations = 'true';
    } else {
      // Children were replaced with skeleton HTML; re-render from card cache (no network requests)
      this.#currentSlideCount = 0;
      this.#prefetchedPdpUrls.clear();
      this.#fetchAndRenderProgressive(this.#cachedSlots, this.#cachedCustomData);
    }
  }

  /**
   * Get the DY selector name from data attribute
   * @returns {string}
   */
  get selectorName() {
    return this.dataset.dySelector || '';
  }

  /**
   * Shopify template name rendered by Liquid ({{ template.name }})
   * @returns {string}
   */
  get templateName() {
    return this.dataset.templateName || '';
  }

  /**
   * Whether the component uses grid layout instead of carousel
   * @returns {boolean}
   */
  get isGridLayout() {
    return this.dataset.layout === 'grid';
  }

  /**
   * Get the image style preference (flat or featured)
   * @returns {string}
   */
  get imageStyle() {
    return this.dataset.imageStyle || 'flat';
  }

  /**
   * Get the section ID based on image style
   * @returns {string}
   */
  get sectionId() {
    return this.imageStyle === 'featured'
      ? 'dy-product-card-rendering-featured'
      : 'dy-product-card-rendering';
  }

  /**
   * Get reference to skeleton container
   * @returns {HTMLElement | null}
   */
  get skeletonEl() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="skeleton"]'));
  }

  /**
   * Get reference to slideshow component container
   * @returns {HTMLElement | null}
   */
  get slideshowEl() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="productList"]'));
  }

  /**
   * Get reference to slideshow slides container
   * @returns {HTMLElement | null}
   */
  get slidesContainerEl() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="scroller"]'));
  }

  /**
   * Resolve DY data (slots, tracking IDs, custom data) eagerly.
   * Called in connectedCallback so DY.Experience.ready is awaited in parallel with
   * the user scrolling toward the section, rather than sequentially after intersection.
   * @returns {Promise<{ slots: DYSlot[], decisionId: string|null, choiceId: string|null, customData: any } | null>}
   */
  async #resolveDyData() {
    if (!this.selectorName) {
      return null;
    }

    // @ts-ignore - DY is a global from dynamic-yield.liquid
    if (!window.DY?.Experience?.ready) {
      return null;
    }

    try {
      // @ts-ignore - DY is a global from dynamic-yield.liquid
      const response = await window.DY.Experience.ready;

      // @ts-ignore - DY is a global from dynamic-yield.liquid
      if (!window.DY.Experience.hasDecision(response, this.selectorName)) {
        return null;
      }

      // @ts-ignore - DY is a global from dynamic-yield.liquid
      const slots = /** @type {DYSlot[]} */ (window.DY.Experience.getSlots(response, this.selectorName));
      if (!slots || slots.length === 0) return null;

      return {
        slots,
        // @ts-ignore - DY is a global from dynamic-yield.liquid
        decisionId: window.DY.Experience.getDecisionId(response, this.selectorName),
        // @ts-ignore - DY is a global from dynamic-yield.liquid
        choiceId: window.DY.Experience.getChoiceId(response, this.selectorName),
        // @ts-ignore - DY is a global from dynamic-yield.liquid
        customData: window.DY.Experience.getCustomData(response, this.selectorName),
      };
    } catch {
      return null;
    }
  }

  /**
   * Load recommendations from DY Experience API.
   * Uses the eagerly-resolved DY data from connectedCallback when available,
   * so only the Section Rendering API fetches remain on the critical path.
   */
  async #loadRecommendations() {
    try {
      const dyData = await this.#earlyDyData;

      if (!dyData) {
        this.#hideSection();
        return;
      }

      this.#decisionId = dyData.decisionId;
      this.#choiceId = dyData.choiceId;

      // Progressive rendering: fetch initial batch first, then remaining
      await this.#fetchAndRenderProgressive(dyData.slots, dyData.customData);
    } catch (error) {
      console.error('[DY Recommendations] Error loading recommendations:', error);
      this.#hideSection();
    }
  }

  /**
   * Get the initial batch size based on viewport width
   * Mobile: 3 cards, Desktop: 7 cards
   * @returns {number}
   */
  get #initialBatchSize() {
    return window.innerWidth < 750 ? 3 : 7;
  }

  /**
   * @typedef {Object} CustomSlideData
   * @property {string} [final_image_mobile] - Mobile image URL
   * @property {string} [final_image_desktop] - Desktop image URL
   * @property {string} [final_image_text] - Text overlay
   * @property {string} [final_image_url] - Link URL
   * @property {string} [overlay_toggle] - Whether to show overlay ("true" or "false")
   */

  /**
   * Fetch and render product cards progressively
   * Renders initial batch first (3 mobile / 7 desktop), then remaining cards
   * @param {DYSlot[]} slots - Array of DY slot objects
   * @param {CustomSlideData | null} [customData] - Custom slide data from DY payload
   */
  async #fetchAndRenderProgressive(slots, customData = null) {
    // Persist for self-healing after section morph resets the component
    this.#cachedSlots = slots;
    this.#cachedCustomData = customData;

    this.#abortController = new AbortController();

    const batchSize = this.#initialBatchSize;
    const initialSlots = slots.slice(0, batchSize);
    const remainingSlots = slots.slice(batchSize);

    // Preload images only for the initial visible batch (not all slots)
    this.#preloadImagesFromSlots(initialSlots);

    // Preload custom slide images if present
    if (customData) {
      if (customData.final_image_desktop) {
        const img = new Image();
        img.src = customData.final_image_desktop;
      }
      if (customData.final_image_mobile && customData.final_image_mobile !== customData.final_image_desktop) {
        const img = new Image();
        img.src = customData.final_image_mobile;
      }
    }

    // Fetch initial batch first (these are visible in viewport)
    const initialCards = await Promise.all(
      initialSlots.map((slot) => this.#fetchSingleCard(slot, { priority: 'high' }))
    );
    const validInitialCards = /** @type {ProductCard[]} */ (initialCards.filter((card) => card !== null));

    if (validInitialCards.length === 0) {
      console.warn('[DY Recommendations] No valid product cards fetched');
      this.#hideSection();
      return;
    }

    // Replace placeholders with real product cards
    this.#renderProductCards(validInitialCards, false, customData);

    // Fetch remaining cards in background if any
    if (remainingSlots.length > 0) {
      const remainingCards = await Promise.all(
        remainingSlots.map((slot) => this.#fetchSingleCard(slot))
      );
      const validRemainingCards = /** @type {ProductCard[]} */ (remainingCards.filter((card) => card !== null));

      if (validRemainingCards.length > 0) {
        this.#appendProductCards(validRemainingCards);
      }
    }
  }

  /**
   * Preload images from DY slots to warm the browser cache
   * Called immediately when DY response is received, before Section Rendering API calls
   * @param {DYSlot[]} slots - Array of DY slot objects
   */
  #preloadImagesFromSlots(slots) {
    const imageUrls = slots
      .map((slot) => slot.productData?.image_url)
      .filter(Boolean);

    // Preload all images immediately (they'll be cached by the browser)
    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }

  /**
   * Fetch a single product card
   * @param {DYSlot} slot - DY slot object
   * @param {{ priority?: 'high' | 'low' | 'auto' }} [options]
   * @returns {Promise<ProductCard | null>}
   */
  async #fetchSingleCard(slot, options = {}) {
    try {
      const productUrl = slot.productData?.url;
      if (!productUrl) {
        console.warn('[DY Recommendations] Slot missing productData.url:', slot);
        return null;
      }

      // Extract product handle from URL
      const handle = this.#extractProductHandle(productUrl);
      if (!handle) {
        console.warn('[DY Recommendations] Could not extract handle from URL:', productUrl);
        return null;
      }

      // Store slotId for click tracking
      if (slot.slotId) {
        this.#slotIdMap.set(handle, slot.slotId);
      }

      // Check cache first
      const cachedHtml = this.#cardCache.get(handle);
      if (cachedHtml) {
      return {
        html: cachedHtml,
        slotId: slot.slotId || '',
        handle,
        sku: slot.productData?.sku || slot.productData?.group_id || '',
      };
      }

      // Fetch product card via Section Rendering API (use locale-aware root)
      const routeRoot = window.Shopify?.routes?.root || '/';
      const apiUrl = new URL(`${routeRoot}products/${handle}`, window.location.origin);
      apiUrl.searchParams.set('section_id', this.sectionId);

      /** @type {RequestInit} */
      const fetchOptions = { signal: this.#abortController?.signal };
      if (options.priority) fetchOptions.priority = options.priority;

      const response = await fetch(apiUrl.href, fetchOptions);

      if (!response.ok) {
        console.warn(`[DY Recommendations] Failed to fetch product card for ${handle}:`, response.status);
        return null;
      }

      const html = await response.text();

      // Cache the result
      this.#cardCache.set(handle, html);

      return {
        html,
        slotId: slot.slotId || '',
        handle,
        sku: slot.productData?.sku || slot.productData?.group_id || '',
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      console.warn('[DY Recommendations] Error fetching product card:', error);
      return null;
    }
  }

  /**
   * Extract product handle from a product URL
   * @param {string} url - Product URL (e.g., "/products/my-product" or full URL)
   * @returns {string | null}
   */
  #extractProductHandle(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const pathParts = urlObj.pathname.split('/products/');
      if (pathParts.length < 2) return null;

      const pathAfterProducts = pathParts[1];
      if (!pathAfterProducts) return null;

      // Get handle and remove any query params or trailing slashes
      const beforeQuery = pathAfterProducts.split('?')[0];
      const handle = beforeQuery ? beforeQuery.split('/')[0] : null;
      return handle || null;
    } catch {
      return null;
    }
  }

  /**
   * Current slide count for indexing when appending
   * @type {number}
   */
  #currentSlideCount = 0;

  /**
   * Render product cards as carousel slides or grid items
   * @param {ProductCard[]} cards
   * @param {boolean} [_isFinalBatch=true] - Whether this is the final batch (for navigation setup)
   * @param {CustomSlideData | null} [customData] - Custom slide data from DY payload
   */
  #renderProductCards(cards, _isFinalBatch = true, customData = null) {
    const slideshowEl = this.slideshowEl;
    const slidesContainerEl = this.slidesContainerEl;
    if (!slideshowEl || !slidesContainerEl) return;

    const itemsHtml = this.isGridLayout
      ? this.#buildGridItemsHtml(cards)
      : this.#buildCarouselSlidesHtml(cards, customData);

    slidesContainerEl.innerHTML = itemsHtml;

    this.#currentSlideCount = cards.length;

    this.#trackImageLoadState(slidesContainerEl);
    this.#attachClickTracking();

    if (!this.isGridLayout) {
      this.#attachCarouselNavigation(slideshowEl, slidesContainerEl, cards.length);
      this.#syncArrowsHeight(slidesContainerEl);
    }

    this.#initializeSwatches();
    this.#rewireQuickAddForDialog(slidesContainerEl);

    this.dataset.loaded = 'true';
    this.dataset.hasRecommendations = 'true';
  }

  /**
   * Build carousel slides HTML
   * @param {ProductCard[]} cards
   * @param {CustomSlideData | null} [customData]
   * @returns {string}
   */
  #buildCarouselSlidesHtml(cards, customData = null) {
    const slidesHtml = cards
      .map(
        (card, index) => `
        <slideshow-slide
          ref="slides[]"
          aria-hidden="${index === 0 ? 'false' : 'true'}"
          style="--slideshow-timeline: --slide-${index};"
          slide-id="dy-rec-${index}"
          class="dy-product-recommendations__slide"
        >
          <div class="resource-list__item" data-slot-id="${card.slotId || ''}" data-product-handle="${card.handle}" data-atc-property-key="_productRecDY" data-atc-property-value="${card.sku}_${this.templateName}_${this.#choiceId || ''}">
            ${card.html}
          </div>
        </slideshow-slide>
      `
      )
      .join('');

    const customSlideHtml = this.#buildCustomSlideHtml(customData, cards.length);
    return slidesHtml + customSlideHtml;
  }

  /**
   * Build grid items HTML (no slideshow wrappers)
   * @param {ProductCard[]} cards
   * @returns {string}
   */
  #buildGridItemsHtml(cards) {
    return cards
      .map(
        (card) => `
        <div class="dy-product-recommendations__grid-item dy-product-recommendations__slide">
          <div class="resource-list__item" data-slot-id="${card.slotId || ''}" data-product-handle="${card.handle}" data-atc-property-key="_productRecDY" data-atc-property-value="${card.sku}_${this.templateName}_${this.#choiceId || ''}">
            ${card.html}
          </div>
        </div>
      `
      )
      .join('');
  }

  /**
   * Build custom slide HTML from DY payload data
   * @param {CustomSlideData | null} customData - Custom slide data
   * @param {number} _slideIndex - Index for the custom slide
   * @returns {string} - HTML string for the custom slide, or empty string if no images
   */
  #buildCustomSlideHtml(customData, _slideIndex) {
    if (!customData) return '';

    const hasDesktopImage = customData.final_image_desktop && customData.final_image_desktop.trim() !== '';
    const hasMobileImage = customData.final_image_mobile && customData.final_image_mobile.trim() !== '';

    // Only create custom slide if we have at least one image
    if (!hasDesktopImage && !hasMobileImage) return '';

    const url = customData.final_image_url || '#';
    const text = customData.final_image_text || '';
    const showOverlay = customData.overlay_toggle === 'true';

    // Build image elements
    let imagesHtml = '';

    if (hasDesktopImage) {
      imagesHtml += `
        <img
          src="${customData.final_image_desktop}"
          alt="${text}"
          class="dy-product-recommendations__custom-image dy-product-recommendations__custom-image--desktop"
          loading="lazy"
        >
      `;
    }

    if (hasMobileImage) {
      imagesHtml += `
        <img
          src="${customData.final_image_mobile}"
          alt="${text}"
          class="dy-product-recommendations__custom-image dy-product-recommendations__custom-image--mobile"
          loading="lazy"
        >
      `;
    } else if (hasDesktopImage) {
      // Fallback to desktop image on mobile if no mobile image set
      imagesHtml += `
        <img
          src="${customData.final_image_desktop}"
          alt="${text}"
          class="dy-product-recommendations__custom-image dy-product-recommendations__custom-image--mobile"
          loading="lazy"
        >
      `;
    }

    // Build overlay HTML if enabled
    const overlayHtml = showOverlay
      ? '<div class="dy-product-recommendations__custom-slide-overlay" style="--overlay-opacity: 0.3;"></div>'
      : '';

    // Build text overlay HTML if text is provided
    const textHtml = text
      ? `<div class="dy-product-recommendations__custom-slide-text">${text}</div>`
      : '';

    return `
      <slideshow-slide
        ref="slides[]"
        class="dy-product-recommendations__slide dy-product-recommendations__custom-slide"
        slide-id="dy-custom-slide"
      >
        <a
          href="${url}"
          class="dy-product-recommendations__custom-slide-link"
        >
          <div class="dy-product-recommendations__custom-slide-media">
            ${imagesHtml}
            ${overlayHtml}
            ${textHtml}
          </div>
        </a>
      </slideshow-slide>
    `;
  }

  /**
   * Append additional product cards to the carousel or grid
   * Used for progressive rendering of remaining cards after initial batch
   * @param {ProductCard[]} cards
   */
  #appendProductCards(cards) {
    const slidesContainerEl = this.slidesContainerEl;
    if (!slidesContainerEl) return;

    const startIndex = this.#currentSlideCount;

    const itemsHtml = this.isGridLayout
      ? this.#buildGridItemsHtml(cards)
      : cards
          .map(
            (card, index) => `
            <slideshow-slide
              ref="slides[]"
              aria-hidden="true"
              style="--slideshow-timeline: --slide-${startIndex + index};"
              slide-id="dy-rec-${startIndex + index}"
              class="dy-product-recommendations__slide"
            >
              <div class="resource-list__item" data-slot-id="${card.slotId || ''}" data-product-handle="${card.handle}" data-atc-property-key="_productRecDY" data-atc-property-value="${card.sku}_${this.templateName}_${this.#choiceId || ''}">
                ${card.html}
              </div>
            </slideshow-slide>
          `
          )
          .join('');

    if (!this.isGridLayout) {
      const customSlide = slidesContainerEl.querySelector('.dy-product-recommendations__custom-slide');
      if (customSlide) {
        customSlide.insertAdjacentHTML('beforebegin', itemsHtml);
      } else {
        slidesContainerEl.insertAdjacentHTML('beforeend', itemsHtml);
      }
    } else {
      slidesContainerEl.insertAdjacentHTML('beforeend', itemsHtml);
    }

    this.#currentSlideCount += cards.length;

    this.#trackImageLoadState(slidesContainerEl);

    // Attach click tracking to new cards
    const selector = this.isGridLayout
      ? `.dy-product-recommendations__grid-item:nth-child(n+${startIndex + 1}) [data-slot-id]`
      : `[slide-id^="dy-rec-"]:nth-child(n+${startIndex + 1}) [data-slot-id]`;
    const newItems = slidesContainerEl.querySelectorAll(selector);
    newItems.forEach((item) => {
      const slotId = /** @type {HTMLElement} */ (item).dataset.slotId;
      if (!slotId) return;
      const links = item.querySelectorAll('a');
      links.forEach((link) => {
        link.addEventListener('click', (e) => {
          this.#handleRecsLinkClick(e, /** @type {HTMLAnchorElement} */ (link), slotId);
        });
      });
      const gallery = item.querySelector('.card-gallery');
      if (gallery) {
        gallery.addEventListener('click', (e) => {
          this.#handleRecsGalleryClick(/** @type {MouseEvent} */ (e), /** @type {HTMLElement} */ (item), slotId);
        });
        this.#attachPdpPrefetchToGallery(/** @type {HTMLElement} */ (gallery), /** @type {HTMLElement} */ (item));
      }
    });

    const allSwatchContainersAfterAppend = Array.from(slidesContainerEl.querySelectorAll('.product-card-swatches'));
    this.#initializeSwatchesForContainers(allSwatchContainersAfterAppend);

    this.#rewireQuickAddForDialog(slidesContainerEl);

    // Re-evaluate carousel arrow states now that new slides have been added.
    // The initial batch may have fit on screen (maxScroll = 0), but with the
    // remaining cards the content now overflows and scrolling should be enabled.
    if (!this.isGridLayout) {
      requestAnimationFrame(() => {
        slidesContainerEl.dispatchEvent(new Event('scroll'));
      });
    }
  }

  /**
   * Sets the arrows container height to match the first card-gallery so arrows
   * are vertically centred on the image area rather than the full slide.
   * @param {HTMLElement} slidesContainerEl
   */
  #syncArrowsHeight(slidesContainerEl) {
    const arrows = /** @type {HTMLElement | null} */ (this.querySelector('slideshow-arrows'));
    if (!arrows) return;

    const sync = () => {
      const gallery = /** @type {HTMLElement | null} */ (slidesContainerEl.querySelector('.card-gallery'));
      if (gallery) {
        arrows.style.height = `${gallery.offsetHeight}px`;
      }
    };

    requestAnimationFrame(sync);
    window.addEventListener('resize', sync);
  }

  /**
   * Attach carousel navigation click handlers
   * @param {HTMLElement} slideshowEl
   * @param {HTMLElement} slidesContainerEl
   * @param {number} _slideCount
   */
  #attachCarouselNavigation(slideshowEl, slidesContainerEl, _slideCount) {
    const prevBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="previous"]'));
    const nextBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="next"]'));

    if (!prevBtn || !nextBtn) return;

    // Remove ref attributes so the base slideshow-component's current setter
    // cannot find these buttons and override our scroll-position-based states
    // with its index-based check (which incorrectly disables arrows when many
    // slides pass the visibility threshold at wider viewports).
    prevBtn.removeAttribute('ref');
    nextBtn.removeAttribute('ref');

    const getSlideWidth = () => {
      const firstSlide = /** @type {HTMLElement | null} */ (slidesContainerEl.querySelector('.dy-product-recommendations__slide'));
      return firstSlide ? firstSlide.offsetWidth + 1 : 221;
    };

    const updateArrowStates = () => {
      const scrollLeft = Math.round(slidesContainerEl.scrollLeft);
      const maxScroll = slidesContainerEl.scrollWidth - slidesContainerEl.clientWidth;

      // Disable previous at start, enable otherwise
      if (scrollLeft <= 1) {
        prevBtn.setAttribute('disabled', '');
      } else {
        prevBtn.removeAttribute('disabled');
      }

      // Disable next at end, enable otherwise
      if (maxScroll <= 0 || scrollLeft >= maxScroll - 1) {
        nextBtn.setAttribute('disabled', '');
      } else {
        nextBtn.removeAttribute('disabled');
      }
    };

    // Handle previous click
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      slidesContainerEl.scrollBy({ left: -getSlideWidth(), behavior: 'smooth' });
      // Track engagement with carousel controls
      this.#reportClick();
    });

    // Handle next click
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      slidesContainerEl.scrollBy({ left: getSlideWidth(), behavior: 'smooth' });
      // Track engagement with carousel controls
      this.#reportClick();
    });

    // Update arrow states on scroll
    slidesContainerEl.addEventListener('scroll', updateArrowStates);

    // Re-evaluate arrow states when the container resizes (viewport width changes
    // alter maxScroll without triggering a scroll event)
    this.#navigationResizeObserver?.disconnect();
    this.#navigationResizeObserver = new ResizeObserver(() => {
      updateArrowStates();
    });
    this.#navigationResizeObserver.observe(slidesContainerEl);

    // Force initial state update (override slideshow-component's disabled state)
    requestAnimationFrame(() => {
      updateArrowStates();
    });
  }

  /**
   * Attach click tracking to product cards (links and product gallery).
   */
  #attachClickTracking() {
    const items = this.querySelectorAll('[data-slot-id]');

    items.forEach((item) => {
      const slotId = /** @type {HTMLElement} */ (item).dataset.slotId;
      if (!slotId) return;

      const links = item.querySelectorAll('a');
      links.forEach((link) => {
        link.addEventListener('click', (e) => {
          this.#handleRecsLinkClick(e, /** @type {HTMLAnchorElement} */ (link), slotId);
        });
      });

      const gallery = item.querySelector('.card-gallery');
      if (gallery) {
        gallery.addEventListener('click', (e) => {
          this.#handleRecsGalleryClick(/** @type {MouseEvent} */ (e), /** @type {HTMLElement} */ (item), slotId);
        });
        this.#attachPdpPrefetchToGallery(/** @type {HTMLElement} */ (gallery), /** @type {HTMLElement} */ (item));
      }
    });
  }

  /**
   * Add a pointerenter listener to a gallery element that speculatively prefetches the PDP page.
   * Uses <link rel="prefetch"> so the browser downloads the page in the background;
   * when the user clicks, the page is already cached and navigation feels instant.
   * @param {HTMLElement} gallery
   * @param {HTMLElement} item - The [data-slot-id] container (to resolve the product link)
   */
  #attachPdpPrefetchToGallery(gallery, item) {
    // No { once: true }: the Set guards against duplicate insertions, and we need to re-fire
    // after swatch clicks update the link href to a different product URL.
    gallery.addEventListener('pointerenter', () => {
      const link = /** @type {HTMLAnchorElement | null} */ (
        item.querySelector('.product-card__link, a[ref="productCardLink"]')
      );
      const href = link?.getAttribute('href');
      if (!href || this.#prefetchedPdpUrls.has(href)) return;
      this.#prefetchedPdpUrls.add(href);
      const el = document.createElement('link');
      el.rel = 'prefetch';
      el.href = href;
      document.head.appendChild(el);
    });
  }

  /**
   * Report a slot click to DY. Returns a promise so callers can delay navigation until sent.
   * @param {string} slotId
   * @returns {Promise<void>}
   */
  #reportSlotClick(slotId) {
    // @ts-ignore - DY is a global from dynamic-yield.liquid
    if (!slotId || !window.DY?.Experience?.reportSlotClick) return Promise.resolve();

    try {
      // @ts-ignore - DY is a global from dynamic-yield.liquid
      return window.DY.Experience.reportSlotClick(slotId);
    } catch (error) {
      console.warn('[DY Recommendations] Error reporting slot click:', error);
      return Promise.resolve();
    }
  }

  /**
   * Handle product card link click: send SLOT_CLICK then navigate (or navigate after timeout).
   * Skips preventDefault for modifier keys (new tab) so the request can complete on the same page.
   * @param {MouseEvent} e
   * @param {HTMLAnchorElement} link
   * @param {string} slotId
   */
  #handleRecsLinkClick(e, link, slotId) {
    const openInNewTab = e.ctrlKey || e.metaKey || e.shiftKey || link.target === '_blank';
    if (openInNewTab) {
      this.#reportSlotClick(slotId);
      return;
    }
    e.preventDefault();

    // If a swatch fetch is in-flight, use the intended product URL instead of the stale href
    const productCard = /** @type {HTMLElement | null} */ (link.closest('product-card'));
    const pendingUrl = productCard?.dataset.pendingProductUrl;
    const navUrl = pendingUrl
      ? new URL(pendingUrl, window.location.origin).pathname
      : (link.getAttribute('href') || link.href);
    if (!navUrl || navUrl.startsWith('javascript:')) return;

    this.#reportSlotClickAndNavigate(slotId, navUrl);
  }

  /**
   * Handle product card gallery click: send SLOT_CLICK then navigate (same as link click).
   * Skips handling when the click is on an interactive element (e.g. quick-add button).
   * @param {MouseEvent} e
   * @param {HTMLElement} item - The [data-slot-id] card container
   * @param {string} slotId
   */
  #handleRecsGalleryClick(e, item, slotId) {
    if (e.target instanceof Element && e.target.closest('button, input, [role="button"], a')) return;

    const link = /** @type {HTMLAnchorElement | null} */ (
      item.querySelector('.product-card__link, .product-card__title, a[ref="productCardLink"]') ||
      item.querySelector('a[href*="/products/"]')
    );
    if (!link) return;

    // If a swatch fetch is in-flight, use the intended product URL instead of the stale href
    const productCard = /** @type {HTMLElement | null} */ (item.querySelector('product-card'));
    const pendingUrl = productCard?.dataset.pendingProductUrl;
    const navUrl = pendingUrl
      ? new URL(pendingUrl, window.location.origin).pathname
      : (link.getAttribute('href') || link.href);
    if (!navUrl || navUrl.startsWith('javascript:')) return;

    e.preventDefault();
    e.stopPropagation();

    const openInNewTab = e.ctrlKey || e.metaKey || e.shiftKey;
    if (openInNewTab) {
      this.#reportSlotClick(slotId);
      window.open(navUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    this.#reportSlotClickAndNavigate(slotId, navUrl);
  }

  /**
   * Report SLOT_CLICK (fire-and-forget) then navigate immediately.
   * The tracking request will complete during page unload — browsers keep in-flight
   * requests alive briefly during same-origin navigation.
   * @param {string} slotId
   * @param {string} navUrl
   */
  #reportSlotClickAndNavigate(slotId, navUrl) {
    this.#reportSlotClick(slotId);
    window.location.href = navUrl;
  }

  /**
   * Report a click engagement to DY (for carousel control interactions)
   */
  #reportClick() {
    // @ts-ignore - DY is a global from dynamic-yield.liquid
    if (!this.#decisionId || !window.DY?.Experience?.reportClick) return;

    try {
      // @ts-ignore - DY is a global from dynamic-yield.liquid
      window.DY.Experience.reportClick(this.#decisionId);
    } catch (error) {
      console.warn('[DY Recommendations] Error reporting click:', error);
    }
  }

  /**
   * Hide the entire section
   */
  #hideSection() {
    this.dataset.hasRecommendations = 'false';
    this.dataset.loaded = 'true';
  }

  /**
   * Track image load state and set data attribute for fade-in effect
   * Uses img.decode() to ensure image is fully decoded before showing content
   * @param {HTMLElement} container - Container with slides to track
   */
  #trackImageLoadState(container) {
    const slides = container.querySelectorAll('.dy-product-recommendations__slide');
    slides.forEach((slide) => {
      const img = /** @type {HTMLImageElement | null} */ (slide.querySelector('.card-gallery__image:not(.card-gallery__image--hover)'));
      if (!img) return;

      const showContent = () => {
        /** @type {HTMLElement} */ (slide).dataset.imageLoaded = 'true';
      };

      // Use decode() to ensure image is fully decoded before showing content
      // This prevents text from appearing before image renders
      if (img.complete && img.naturalHeight !== 0) {
        // Image already loaded, decode to ensure it's ready to render
        if (typeof img.decode === 'function') {
          img.decode().then(showContent).catch(showContent);
        } else {
          // Fallback for browsers without decode support
          requestAnimationFrame(showContent);
        }
      } else {
        // Wait for image to load, then decode
        img.addEventListener('load', () => {
          if (typeof img.decode === 'function') {
            img.decode().then(showContent).catch(showContent);
          } else {
            requestAnimationFrame(showContent);
          }
        }, { once: true });

        // Handle error case - still show content
        img.addEventListener('error', showContent, { once: true });
      }
    });
  }

  /**
   * Get (or lazily create) the prefetch state object for a swatch container.
   * @param {Element} container
   * @returns {{ cache: Map<string, Element>, abortController: AbortController|null, timeoutId: ReturnType<typeof setTimeout>|null }}
   */
  #getSwatchState(container) {
    let state = this.#swatchPrefetchState.get(container);
    if (!state) {
      state = { cache: new Map(), abortController: null, timeoutId: null };
      this.#swatchPrefetchState.set(container, state);
    }
    return state;
  }

  /**
   * Cancel any pending hover-prefetch timer and in-flight fetch for a container.
   * @param {{ abortController: AbortController|null, timeoutId: ReturnType<typeof setTimeout>|null }} state
   */
  #cancelSwatchPrefetch(state) {
    if (state.timeoutId != null) {
      clearTimeout(state.timeoutId);
      state.timeoutId = null;
    }
    if (state.abortController) {
      state.abortController.abort();
      state.abortController = null;
    }
  }

  /**
   * Preloads the primary image from a fetched card before the DOM is swapped.
   * Resolves when loaded or after a timeout so it never blocks indefinitely.
   * @param {Element} newCard
   * @returns {Promise<void>}
   */
  #preloadSwatchImage(newCard) {
    return new Promise((resolve) => {
      const primaryImg = /** @type {HTMLImageElement | null} */ (
        newCard.querySelector('.card-gallery__image:not(.card-gallery__image--hover)')
      );
      if (!primaryImg?.src) return resolve();

      const img = new Image();
      const timeoutId = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        resolve();
      }, 3000);
      const done = () => { clearTimeout(timeoutId); resolve(); };
      img.onload = done;
      img.onerror = done;
      // Copy srcset + sizes so the browser preloads the same URL it will actually render,
      // not the largest srcset candidate (which is what it picks without a sizes hint).
      if (primaryImg.srcset) img.srcset = primaryImg.srcset;
      if (primaryImg.sizes) img.sizes = primaryImg.sizes;
      img.src = primaryImg.src;
    });
  }

  /**
   * Prefetch a swatch card and warm its primary image in the browser cache.
   * Fire-and-forget — results are stored in the container's state cache.
   * @param {Element} container
   * @param {string} productUrl
   */
  async #prefetchSwatchCard(container, productUrl) {
    const state = this.#getSwatchState(container);
    if (state.cache.has(productUrl)) return;

    state.abortController = new AbortController();
    const apiUrl = new URL(productUrl, window.location.origin);
    apiUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(apiUrl.toString(), { signal: state.abortController.signal });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newCard = doc.querySelector('product-card');
      if (!newCard) return;

      // Evict oldest entry if at capacity
      if (state.cache.size >= DYProductRecommendations.#SWATCH_CACHE_MAX) {
        const firstKey = state.cache.keys().next().value;
        if (firstKey != null) state.cache.delete(firstKey);
      }
      state.cache.set(productUrl, newCard);

      // Fire-and-forget: warm the browser image cache so the swap appears instant on click
      const primaryImg = /** @type {HTMLImageElement | null} */ (
        newCard.querySelector('.card-gallery__image:not(.card-gallery__image--hover)')
      );
      if (primaryImg?.src) {
        const img = new Image();
        if (primaryImg.srcset) img.srcset = primaryImg.srcset;
        if (primaryImg.sizes) img.sizes = primaryImg.sizes;
        img.src = primaryImg.src;
      }
    } catch {
      // Ignore AbortError and other prefetch errors
    } finally {
      state.abortController = null;
    }
  }

  /**
   * Initialize product card swatches with click handlers
   */
  #initializeSwatches() {
    const swatchContainers = this.querySelectorAll('.product-card-swatches');
    this.#initializeSwatchesForContainers(swatchContainers);
  }

  /**
   * Initialize swatches for specific containers.
   * Binds hover prefetch (pointerenter/leave) and click handling with cache + deferred loading state.
   * @param {NodeListOf<Element> | Element[]} swatchContainers
   */
  #initializeSwatchesForContainers(swatchContainers) {
    swatchContainers.forEach((container) => {
      if (this.#initializedSwatchContainers.has(container)) return;
      this.#initializedSwatchContainers.add(container);

      // Hover prefetch: start fetching card HTML + image after a short delay
      container.addEventListener('pointerenter', (e) => {
        const button = /** @type {Element | null} */ (/** @type {Element} */ (e.target).closest('.product-card-swatch'));
        if (!button || /** @type {HTMLButtonElement} */ (button).disabled) return;
        const { productUrl } = /** @type {HTMLElement} */ (button).dataset;
        if (!productUrl) return;

        const state = this.#getSwatchState(container);
        this.#cancelSwatchPrefetch(state);
        state.timeoutId = setTimeout(() => {
          state.timeoutId = null;
          this.#prefetchSwatchCard(container, productUrl);
        }, DYProductRecommendations.#SWATCH_HOVER_DELAY_MS);
      }, true);

      container.addEventListener('pointerleave', (e) => {
        const button = /** @type {Element | null} */ (/** @type {Element} */ (e.target).closest('.product-card-swatch'));
        if (button) this.#cancelSwatchPrefetch(this.#getSwatchState(container));
      }, true);

      container.addEventListener('click', async (e) => {
        const target = /** @type {Element | null} */ (e.target);
        const button = /** @type {HTMLButtonElement | null} */ (target?.closest('.product-card-swatch'));
        if (!button || button.disabled) return;

        e.preventDefault();
        e.stopPropagation();

        const { productUrl } = /** @type {HTMLElement} */ (button).dataset;
        if (!productUrl) return;

        const productCard = button.closest('product-card');
        if (!productCard) return;

        const state = this.#getSwatchState(container);
        this.#cancelSwatchPrefetch(state);

        // Set active state
        const currentActive = container.querySelector('.product-card-swatch--active');
        currentActive?.classList.remove('product-card-swatch--active');
        button.classList.add('product-card-swatch--active');

        // Mark the card with the intended destination so click handlers can use it
        // even if the card fetch hasn't resolved yet (prevents navigating to wrong product)
        const productCardEl = /** @type {HTMLElement} */ (productCard);
        productCardEl.dataset.pendingProductUrl = productUrl;

        // Defer loading state so cache hits don't briefly dim the gallery
        const gallery = /** @type {HTMLElement | null} */ (productCard.querySelector('.card-gallery'));
        const loadingTimerId = setTimeout(() => {
          if (gallery) {
            gallery.style.opacity = '0.6';
            gallery.style.transition = 'opacity 0.2s ease';
          }
        }, DYProductRecommendations.#SWATCH_LOADING_DEFER_MS);

        try {
          // Use prefetched card from cache if available, otherwise fetch now
          let newCard = state.cache.get(productUrl) ?? null;
          const wasCached = newCard !== null;
          if (newCard) {
            state.cache.delete(productUrl);
          } else {
            const apiUrl = new URL(productUrl, window.location.origin);
            apiUrl.searchParams.set('section_id', this.sectionId);
            const response = await fetch(apiUrl.href);
            if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            newCard = doc.querySelector('product-card');
          }

          if (newCard) {
            // Cache hit: image was already warming in browser cache during hover — swap immediately.
            // Cache miss: wait for image so it doesn't flash in blank before rendering.
            if (!wasCached) await this.#preloadSwatchImage(newCard);
            this.#updateProductCard(productCard, newCard);
          }
        } catch (error) {
          console.error('[DY Recommendations] Swatch update error:', error);
        } finally {
          clearTimeout(loadingTimerId);
          if (gallery) gallery.style.opacity = '';
          delete productCardEl.dataset.pendingProductUrl;
        }
      });
    });
  }

  /**
   * Update product card with new content
   * @param {Element} currentCard
   * @param {Element} newCard
   */
  #updateProductCard(currentCard, newCard) {
    // Update product link
    const currentLink = currentCard.querySelector('.product-card__title');
    const newLink = newCard.querySelector('.product-card__title');
    if (currentLink && newLink) {
      currentLink.setAttribute('href', newLink.getAttribute('href') || '');
      currentLink.innerHTML = newLink.innerHTML;
    }

    // Update accessibility link (product-card__link / ref="productCardLink")
    // This is the first selector found by the gallery click handler, so it must stay in sync with the swatch
    const currentAccessLink = currentCard.querySelector('.product-card__link, a[ref="productCardLink"]');
    const newAccessLink = newCard.querySelector('.product-card__link, a[ref="productCardLink"]');
    if (currentAccessLink && newAccessLink) {
      currentAccessLink.setAttribute('href', newAccessLink.getAttribute('href') || '');
    }

    // Update gallery anchor links (cardGalleryLink wraps the slideshow images, productTitleLink
    // is shown when a product has no media — both must point to the selected color product)
    const galleryLinkSelectors = 'a[ref="cardGalleryLink"], a[ref="productTitleLink"]';
    currentCard.querySelectorAll(galleryLinkSelectors).forEach((currentGalleryLink, i) => {
      const newGalleryLinks = newCard.querySelectorAll(galleryLinkSelectors);
      const newGalleryLink = newGalleryLinks[i];
      if (newGalleryLink) {
        currentGalleryLink.setAttribute('href', newGalleryLink.getAttribute('href') || '');
      }
    });

    // Update price
    const currentPrice = currentCard.querySelector('product-price');
    const newPrice = newCard.querySelector('product-price');
    if (currentPrice && newPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }

    // Update product ID
    const newProductId = newCard.getAttribute('data-product-id');
    if (newProductId) {
      currentCard.setAttribute('data-product-id', newProductId);
    }

    // Update gallery images in-place (mutate existing <img> attributes rather than replacing
    // innerHTML) and batch with rAF — matches the native product-card-swatches pattern.
    const currentImgs = /** @type {NodeListOf<HTMLImageElement>} */ (
      currentCard.querySelectorAll('.card-gallery__media img')
    );
    const newImgs = /** @type {NodeListOf<HTMLImageElement>} */ (
      newCard.querySelectorAll('.card-gallery__media img')
    );
    const currentGalleryContainer = currentCard.querySelector('.card-gallery');
    const newGalleryContainer = newCard.querySelector('.card-gallery');

    requestAnimationFrame(() => {
      // Update src/srcset/alt on matching existing elements — no DOM node creation
      currentImgs.forEach((img, i) => {
        const newImg = newImgs[i];
        if (!newImg) return;
        img.srcset = newImg.srcset || '';
        img.src = newImg.src;
        img.alt = newImg.alt;
      });

      // Sync gallery aspect-ratio style
      if (currentGalleryContainer && newGalleryContainer) {
        const newStyle = newGalleryContainer.getAttribute('style');
        if (newStyle) currentGalleryContainer.setAttribute('style', newStyle);
      }
    });

    // Sync quick-add-component attributes so add-to-cart targets the swatch-selected product.
    // Without this, the quick-add still references the original product's handle/id/image/template.
    const currentQuickAdd = /** @type {HTMLElement | null} */ (currentCard.querySelector('quick-add-component'));
    const newQuickAdd = /** @type {HTMLElement | null} */ (newCard.querySelector('quick-add-component'));
    if (currentQuickAdd && newQuickAdd) {
      for (const attr of ['data-product-handle', 'data-product-id', 'data-product-title', 'data-product-image']) {
        const val = newQuickAdd.getAttribute(attr);
        if (val != null) {
          currentQuickAdd.setAttribute(attr, val);
        } else {
          currentQuickAdd.removeAttribute(attr);
        }
      }

      // Replace the quick-add template so size options reflect the new product
      const currentTemplate = currentQuickAdd.querySelector('[data-quick-add-template]');
      const newTemplate = newQuickAdd.querySelector('[data-quick-add-template]');
      if (currentTemplate && newTemplate) {
        currentTemplate.innerHTML = newTemplate.innerHTML;
      } else if (newTemplate) {
        currentQuickAdd.appendChild(newTemplate.cloneNode(true));
      }
    }
  }
}

if (!customElements.get('dy-product-recommendations')) {
  customElements.define('dy-product-recommendations', DYProductRecommendations);
}
